import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UPDATE_OVERLAY_ANIMATIONS } from '../../../core/constants/animations';
import {
  UPDATE_STATE,
  UPDATE_STEP,
  UPDATE_STEPS,
  UPDATE_STEP_MS,
  UpdateStep,
  VersionPayload,
} from '../../../core/constants/update.constants';
import { UpdateService } from '../../../core/services/ui/update.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-update-overlay',
  templateUrl: './update-overlay.component.html',
  styleUrls: ['./update-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [...UPDATE_OVERLAY_ANIMATIONS],
})
export class UpdateOverlayComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  public readonly STEP = UPDATE_STEP;
  public readonly steps = UPDATE_STEPS;

  public visible = false;
  public updating = false;
  public currentStep: UpdateStep = UPDATE_STEP.IDLE;
  public latestVersion: VersionPayload | null = null;

  constructor(
    private updateService: UpdateService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.updateService.start();

    this.updateService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.visible = state === UPDATE_STATE.BLOCKING;
        this.cdr.markForCheck();
      });

    this.updateService.latestVersion$
      .pipe(takeUntil(this.destroy$))
      .subscribe((version) => {
        this.latestVersion = version;
        this.cdr.markForCheck();
      });
  }

  public applyUpdate(): void {
    if (this.updating) {
      return;
    }
    this.updating = true;
    this.runUpdateSequence();
  }

  public isStepActive(step: UpdateStep): boolean {
    return this.currentStep === step;
  }

  public isStepDone(step: UpdateStep): boolean {
    const order = this.steps.map((s) => s.key);
    const currentIndex = order.indexOf(this.currentStep);
    const stepIndex = order.indexOf(step);
    return currentIndex > stepIndex;
  }

  public trackByStep(_index: number, step: { key: UpdateStep }): UpdateStep {
    return step.key;
  }

  private runUpdateSequence(): void {
    this.currentStep = UPDATE_STEP.CLEARING_CACHE;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.clearBrowserState();
      this.currentStep = UPDATE_STEP.CLOSING_SESSION;
      this.cdr.markForCheck();

      setTimeout(() => {
        this.closeSession();
        this.currentStep = UPDATE_STEP.RELOADING;
        this.cdr.markForCheck();

        setTimeout(() => {
          this.persistAcceptedVersion();
          this.hardReload();
        }, UPDATE_STEP_MS.RELOADING);
      }, UPDATE_STEP_MS.CLOSING_SESSION);
    }, UPDATE_STEP_MS.CLEARING_CACHE);
  }

  private clearBrowserState(): void {
    try {
      sessionStorage.clear();
    } catch {
    }

    try {
      const version = this.latestVersion?.version ?? null;
      localStorage.clear();
      if (version) {
        this.updateService.acceptVersion(version);
      }
    } catch {
      // ignore
    }

    try {
      this.clearAllCookies();
    } catch {
      // ignore
    }

    if ('caches' in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => undefined);
    }
  }

  private clearAllCookies(): void {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    const hostname = window.location.hostname;
    const domains = [hostname, `.${hostname}`];

    cookies.forEach((cookie) => {
      const eqIndex = cookie.indexOf('=');
      const name = (eqIndex > -1 ? cookie.substring(0, eqIndex) : cookie).trim();
      if (!name) {
        return;
      }
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      });
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  }

  private closeSession(): void {
    try {
      this.authService.logOut();
      this.authService.performLogout();
      this.authService.lock();
    } catch {
      return;
    }
  }

  private persistAcceptedVersion(): void {
    const version = this.latestVersion?.version;
    if (version) {
      this.updateService.acceptVersion(version);
    }
  }

  private hardReload(): void {
    const target = `${window.location.pathname}?u=${Date.now()}`;
    window.location.replace(target);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
